import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogIn, LogOut, User, ShieldCheck, Phone } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { NotificationBell } from "./NotificationBell";

const NAV = [
  { label: "Služby", href: "#cenik" },
  { label: "O mně", href: "#o-mne" },
  { label: "Galerie", href: "#galerie" },
  { label: "Jak to funguje", href: "#jak-to-funguje" },
  { label: "FAQ", href: "#faq" },
  { label: "Kontakt", href: "#kontakt" },
];

const PHONE = "+420 604 447 377";
const PHONE_RAW = "+420604447377";

function scrollToHash(href) {
  const id = href.replace(/^#/, "");
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  else window.location.href = `/${href}`;
}

export default function Navbar({ onOpenAuth, onOpenBooking, onOpenDashboard }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isStaff = user && (user.role === "admin" || user.role === "employee");

  return (
    <header
      className="fixed top-0 inset-x-0 z-40 border-b border-linka-dark"
      style={{
        background: "var(--uhel-900)",
        borderBottomWidth: "0.5px",
      }}
      data-testid="navbar"
    >
      <div className="max-w-[1180px] mx-auto px-[22px] md:px-8 h-16 flex items-center justify-between">
        <a href="#hero" className="flex flex-col leading-tight" data-testid="logo">
          <span className="font-display text-krem-100" style={{ fontSize: "21.9px" }}>Apex Detailing</span>
          <span className="text-tlum-dark tracking-[0.06em]" style={{ fontSize: "14.7px" }}>Praha</span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={(e) => { e.preventDefault(); scrollToHash(n.href); }}
              className="text-[14px] text-tlum-dark hover:text-krem-100 transition-colors duration-150"
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={`tel:${PHONE_RAW}`} className="text-[13px] text-tlum-dark hover:text-krem-100 transition-colors duration-150" data-testid="navbar-phone">
            {PHONE}
          </a>
          {!user ? (
            <button onClick={onOpenAuth} className="text-[13px] text-tlum-dark hover:text-krem-100 transition-colors duration-150" data-testid="navbar-login-btn">
              <LogIn className="inline w-3.5 h-3.5 mr-1" /> Přihlásit se
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <NotificationBell onOpenDashboard={onOpenDashboard} />
              {isStaff && (
                <button onClick={onOpenDashboard} className="text-[13px] text-akcent hover:text-krem-100 transition-colors duration-150" data-testid="navbar-dashboard-btn">
                  <ShieldCheck className="inline w-3.5 h-3.5 mr-1" /> Admin
                </button>
              )}
              <button onClick={onOpenDashboard} className="text-[13px] text-tlum-dark hover:text-krem-100 transition-colors duration-150" data-testid="navbar-account-btn">
                <User className="inline w-3.5 h-3.5 mr-1" /> {user.firstName}
              </button>
              <button onClick={logout} className="text-tlum-dark hover:text-krem-100 transition-colors duration-150" data-testid="navbar-logout-btn" aria-label="Odhlásit">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
          <button onClick={onOpenBooking} className="btn-accent navbar-btn-accent !py-2 !px-4 !text-sm !rounded-lg" data-testid="navbar-book-btn">
            Rezervovat
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          {user && <NotificationBell onOpenDashboard={onOpenDashboard} />}
          <a href={`tel:${PHONE_RAW}`} className="btn-accent !py-2 !px-3 !text-[13px] !rounded-lg" data-testid="navbar-mobile-call">
            <Phone className="w-3.5 h-3.5" /> <span className="hidden min-[400px]:inline">Zavolat</span>
          </a>
          <button className="text-krem-100 p-1" onClick={() => setMobileOpen((v) => !v)} data-testid="navbar-mobile-toggle" aria-label="Menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24 }}
            className="lg:hidden overflow-hidden border-t border-linka-dark"
            style={{ background: "var(--uhel-900)", borderTopWidth: "0.5px" }}
          >
            <div className="px-[22px] py-6 flex flex-col gap-4 max-h-[calc(100vh-64px)] overflow-y-auto">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileOpen(false);
                    setTimeout(() => scrollToHash(n.href), 200);
                  }}
                  className="text-[15px] text-krem-100/90 py-1"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {n.label}
                </a>
              ))}
              <div className="border-t border-linka-dark pt-4 flex flex-col gap-3" style={{ borderTopWidth: "0.5px" }}>
                {!user ? (
                  <button onClick={() => { setMobileOpen(false); onOpenAuth(); }} className="btn-outline !text-sm !py-2.5 !px-4 !rounded-lg" data-testid="mobile-login-btn">Přihlásit se</button>
                ) : (
                  <>
                    <button onClick={() => { setMobileOpen(false); onOpenDashboard(); }} className="btn-outline !text-sm !py-2.5 !px-4 !rounded-lg">Můj účet</button>
                    <button onClick={() => { setMobileOpen(false); logout(); }} className="text-[14px] text-tlum-dark text-left">Odhlásit</button>
                  </>
                )}
                <button onClick={() => { setMobileOpen(false); onOpenBooking(); }} className="btn-accent navbar-btn-accent !text-sm !py-2.5 !px-4 !rounded-lg" data-testid="mobile-book-btn">Rezervovat termín</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
